import { FieldPath } from 'react-hook-form';
import { utcToZonedTime } from 'date-fns-tz';
import { eachDayOfInterval, format } from 'date-fns';

import { Availability, TimeInterval } from './useSchedulingPreferencesForm/types';
import { SchedulingHour } from '../../hooks/useSchedulingPreferences/types';
import { SchedulingPreferencesFormValues } from './useSchedulingPreferencesForm/types';

export const DATE_FORMAT = 'yyyy-MM-dd';
export const START_HOUR_TIME = 'T00:00:00Z';
export const END_HOUR_TIME = 'T23:59:59Z';

export const intervalsOverlap = (interval1: TimeInterval, interval2: TimeInterval) => {
    const sameInterval = interval1.start === interval2.start && interval1.end === interval2.end;
    const leftRightOverlap = interval1.end > interval2.start && interval1.start < interval2.end;
    const rightLeftOverlap = interval2.end > interval1.start && interval2.start < interval1.end;
    return leftRightOverlap || rightLeftOverlap || sameInterval;
};

export const validateEmptyInterval = (availability: Availability['availability']) => {
    const errors: { field: FieldPath<Availability>; message: string }[] = [];
    availability?.forEach((currentDay, dayIndex) => {
        let errorField = '';
        currentDay.intervals.forEach(interval => {
            if (interval.start === null || interval.end === null) {
                if (interval.start === null && interval.end === null) {
                    errorField = 'Start time and end time';
                } else {
                    errorField = interval.start === null ? 'Start time' : 'End time';
                }
                errors.push({
                    field: `availability.${dayIndex}.date`,
                    message: `${errorField} can't be empty`,
                });
            }
        });
    });
    return errors;
};

const validatePositiveInterval = (availability: Availability['availability']) => {
    const errors: { field: FieldPath<Availability>; message: string }[] = [];
    availability?.forEach((currentDay, dayIndex) => {
        currentDay.intervals.forEach(interval => {
            if (
                interval.start !== null &&
                interval.end !== null &&
                interval.start >= interval.end
            ) {
                errors.push({
                    field: `availability.${dayIndex}.date`,
                    message: 'End times must be after start times',
                });
            }
        });
    });
    return errors;
};

const validateNoOverlap = (availability: Availability['availability']) => {
    const errors: { field: FieldPath<Availability>; message: string }[] = [];
    availability?.forEach((currentDay, dayIndex) => {
        currentDay.intervals.forEach((interval, intervalIndex) => {
            if (
                currentDay.intervals.find(
                    (otherInterval, i) =>
                        i !== intervalIndex && intervalsOverlap(interval, otherInterval)
                )
            ) {
                errors.push({
                    field: `availability.${dayIndex}.date`,
                    message: 'Two time blocks cannot overlap',
                });
            }
        });
    });
    return errors;
};

const validators = [validatePositiveInterval, validateNoOverlap];

export const validateAvailability = (availability: Availability['availability']) => {
    return validators.reduce((acc, validator) => [...acc, ...validator(availability)], []);
};

export const prepareAvailabilityByTotalTime = (
    availability: SchedulingPreferencesFormValues['availability'],
    schedulingHoursBase: SchedulingHour[],
    currentTimeZone: string
) => {
    if (!schedulingHoursBase || schedulingHoursBase.length === 0) {
        return availability;
    }

    const start = utcToZonedTime(
        `${schedulingHoursBase[0].date}${START_HOUR_TIME}`,
        currentTimeZone
    );

    const end = utcToZonedTime(
        `${schedulingHoursBase[schedulingHoursBase.length - 1].date}${END_HOUR_TIME}`,
        currentTimeZone
    );

    const availabilityDates = eachDayOfInterval({ start, end }).map(day =>
        format(day, DATE_FORMAT)
    );

    return availabilityDates.map(availabilityDate => {
        const day = availability.find(day => day.date === availabilityDate);
        const isDayAndIntervalsExist =
            day && !day.intervals.every(interval => interval.start === 0 && interval.end === 0);

        return isDayAndIntervalsExist
            ? day
            : {
                  date: availabilityDate,
                  intervals: [],
              };
    });
};
